/**
 * Stripe Checkout helper.
 *
 * Stripe Checkout Sessions are hosted pages — the FE doesn't render the card
 * form. We just navigate to `session.url` and Stripe brings the user back to
 * our `success_url` after payment.
 *
 * This module exposes:
 *   - redirectToCheckout(url)  — simple window.location redirect (works for both modes)
 *   - parseRedirectParams()    — read ?billing=... &session_id=... from current URL
 *   - clearRedirectParams()    — strip the params after handling
 */

export type RedirectStatus =
  | { kind: "success";        sessionId: string | null }   // subscription completed
  | { kind: "addon_success";  sessionId: string | null }   // addon purchase completed
  | { kind: "cancelled";      sessionId: null }            // user clicked cancel on Stripe
  | { kind: "none" };

export function redirectToCheckout(checkoutUrl: string) {
  window.location.assign(checkoutUrl);
}

export function parseRedirectParams(): RedirectStatus {
  if (typeof window === "undefined") return { kind: "none" };
  const params = new URLSearchParams(window.location.search);
  const billing = params.get("billing");
  const sessionId = params.get("session_id");

  if (billing === "success")        return { kind: "success",       sessionId };
  if (billing === "addon_success")  return { kind: "addon_success", sessionId };
  if (billing === "cancelled")      return { kind: "cancelled",     sessionId: null };
  return { kind: "none" };
}

/**
 * Remove the billing query params from the URL after we've handled them, so
 * a refresh doesn't re-trigger any post-checkout logic.
 */
export function clearRedirectParams() {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  url.searchParams.delete("billing");
  url.searchParams.delete("session_id");
  window.history.replaceState({}, "", url.pathname + (url.search ? `?${url.searchParams}` : ""));
}
